import type { ContactJson } from 'app/types/entities'
import type { TaskTemplate } from 'app/generated/graphql'
import { substituteTokens } from 'app/lib/messageTemplates'

export type ApplyTaskTemplateContext = {
  /** Full or partial contact — name-derived tokens resolve from `name` alone. */
  contact?: Partial<ContactJson>
  /**
   * Active workspace staff ids for the stale-owner check. Omit when the
   * surface has no list to check against — the ownerId passes through and
   * the surface's own picker will show/reject it.
   */
  activeStaffIds?: string[]
  /** Test seam; defaults to the current time. */
  now?: Date
}

export type AppliedTaskTemplate = {
  title: string
  description?: string
  /** Local start-of-day, `now` + dueInDays. Surfaces own their serialization. */
  dueDate?: Date
  ownerId?: string
}

/**
 * Resolve a task template into concrete form values. Tokens resolve with
 * keepUnresolved — tasks are internal artifacts, so an unresolved token is a
 * visible, editable nuisance rather than a send-safety problem. A stale
 * ownerId silently falls back to the surface default (spec: a template never
 * errors because someone left the practice).
 */
export function applyTaskTemplate(template: TaskTemplate, context: ApplyTaskTemplateContext): AppliedTaskTemplate {
  const tokenContext = { contact: context.contact as ContactJson | undefined }
  const substitutionOpts = { keepUnresolved: true }
  const title = substituteTokens(template.title, tokenContext, substitutionOpts).text
  const description = template.description
    ? substituteTokens(template.description, tokenContext, substitutionOpts).text
    : undefined

  let dueDate: Date | undefined
  if (template.dueInDays != null) {
    const base = context.now ?? new Date()
    dueDate = new Date(base.getFullYear(), base.getMonth(), base.getDate() + template.dueInDays)
  }

  let ownerId = template.ownerId ?? undefined
  if (ownerId && context.activeStaffIds && !context.activeStaffIds.includes(ownerId)) {
    ownerId = undefined
  }

  return { title, description, dueDate, ownerId }
}
